import {
  getAllIds,
  getCountByStatus,
  useGetAllPhotographies,
} from "@/api/photography/photography";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import NewPhotographyDialog from "@/components/photography/NewPhotographyDialog";
import PhotographyPrintDrawer from "@/components/photography/PhotographyPrintDrawer";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DashboardListGridLayout } from "@/components/dashboard/DashboardListGridLayout";
import { GridCardWithActions } from "@/components/links/GridCardWithActions";
import { PhotographyLinkGridCard } from "@/components/links/relatedLinkGridCards";
import { DashboardGridCardActions } from "@/components/table/DashboardGridCardActions";
import DashboardSelectionBar from "@/components/table/DashboardSelectionBar";
import { DataTable } from "@/components/ui/datatable";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import {
  photographiesAdvancedSearchConfig,
  photographiesColumns,
} from "@/config/dashboard/photographies";
import { useDashboardListScrollContext } from "@/hooks/useDashboardListScrollContext";
import { useDashboardActionsColumn } from "@/hooks/useDashboardActionsColumn";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { useDashboardRowSelection } from "@/hooks/useDashboardRowSelection";
import { Photography } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ACTIONS_COLUMN_ID,
  ensureActionsColumnLast,
} from "@/components/table/table-actions-column";

const STORAGE_KEY = "photography-table-preferences";

const flatVisiblePhotographyColumns = [
  ...photographiesColumns.flatMap((column: ColumnFormatted) =>
    column.visible ? column.key : [],
  ),
  ACTIONS_COLUMN_ID,
];

const PhotographyDashboard = () => {
  const { t } = useTranslation();

  const {
    pagination,
    setPagination,
    countByStatus,
    currentCount,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<Photography>({
    resourceName: "photography",
    resourceCounter: getCountByStatus,
    statusField: "photosPublicationStatus",
  });

  const { data, isFetching } = useGetAllPhotographies(fetchParams);
  const {
    listKey,
    navigateToFiche,
    entityIds,
    isLoading: listScrollLoading,
    listPagination,
  } = useDashboardListScrollContext(data, pagination, isFetching, STORAGE_KEY);

  const actionsColumn = useDashboardActionsColumn<Photography>({
    documentType: "photography",
    publicationStatusField: "photosPublicationStatus",
    onEdit: (row) =>
      navigateToFiche(`/edit/photography/${row.id}`, row.id),
  });

  const columns: ColumnDef<Photography, any>[] = useMemo(() => {
    return [...generateTable(photographiesColumns, "photography"), actionsColumn];
  }, [actionsColumn]);


  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(STORAGE_KEY);
    if (saved) {
      const { order } = saved;
      return ensureActionsColumnLast(order || flatVisiblePhotographyColumns);
    }
    return flatVisiblePhotographyColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { visibility } = saved;
        return (
          visibility || {
            ...photographiesColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...photographiesColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  const {
    selectionState,
    setSelectionState,
    dataTableSelection,
    isSelected,
    setRowSelected,
  } = useDashboardRowSelection<Photography>({
      getAllRowIds: () => getAllIds(fetchParams.where),
    });

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">{t("dashboardTitlePhotos")}</p>
        <GlobalSearch resource="photographies"
          config={photographiesAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={photographiesAdvancedSearchConfig}
            resource="photographies"
          />
          <AdvancedSort config={photographiesAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createPphotography")}
            Dialog={NewPhotographyDialog}
            batchImportType="photography"
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DashboardListGridLayout
          storageKey={STORAGE_KEY}
          listKey={listKey}
          entityIds={entityIds}
          isLoading={listScrollLoading}
          listPagination={listPagination}
          setListPagination={setPagination}
          listTable={
            <DataTable
              onRowClick={(row) =>
                navigateToFiche(`/edit/photography/${row.original.id}`, row.original.id)
              }
              getListItemId={(row) => row.original.id}
              pagination={{
                pagination: pagination,
                setPage: setPagination,
                totalSize: currentCount,
              }}
              columns={columns}
              data={data ?? []}
              storageKey={STORAGE_KEY}
              options={{
                enableColumnResizing: true,
                state: {
                  columnVisibility: columnVisibility,
                  columnOrder: columnsOrder,
                },
                onColumnVisibilityChange: setColumnVisibility,
                onColumnOrderChange: setColumnsOrder,
              }}
              selection={dataTableSelection}
            />
          }
          gridContent={
            <>
              {(data ?? []).map((item) => (
                <GridCardWithActions
                  key={item.id}
                  selected={isSelected(item.id)}
                  onSelectedChange={(checked) =>
                    setRowSelected(item.id, checked)
                  }
                  actions={
                    <DashboardGridCardActions
                      documentType="photography"
                      item={item}
                      publicationStatusField="photosPublicationStatus"
                      onEdit={(row) =>
                        navigateToFiche(`/edit/photography/${row.id}`, row.id)
                      }
                    />
                  }
                >
                  <PhotographyLinkGridCard
                    item={item}
                    t={t}
                    onTextClick={() =>
                      navigateToFiche(`/edit/photography/${item.id}`, item.id)
                    }
                  />
                </GridCardWithActions>
              ))}
            </>
          }
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
        />
        <DashboardSelectionBar
          documentType="photography"
          selectionState={selectionState}
          setSelectionState={setSelectionState}
        >
          {(selectedIds) => (
            <PhotographyPrintDrawer selectedPhotographiesIds={selectedIds} />
          )}
        </DashboardSelectionBar>
      </div>
    </div>
  );
};

export default PhotographyDashboard;
